import React, { useMemo, useEffect } from "react";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { StatusesContent } from "./StatusesContent";
import { useStatusManagement } from "./hooks";
import { ProjectWithStatuses } from "@/utils/types";
import { getColumnConfig } from "./utils/columnConfigUtils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

export interface ProjectStatus {
  id: string;
  name: string;
  type: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  order?: number;
  defaultStatusKey?: string;
}

interface ProjectConfigProps {
  project: ProjectWithStatuses;
}

const ProjectConfig: React.FC<ProjectConfigProps> = React.memo(
  ({ project }) => {
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
    const fetchIssuesForProject = useIssuesStore(
      (store) => store.fetchIssuesForProject,
    );
    const currentProjectIssues = useIssuesStore(
      (store) => store.issuesByProject[project?.id],
    );

    // Fetch issues if not already loaded
    useEffect(() => {
      if (
        project?.id &&
        !currentProjectIssues &&
        supabaseClient &&
        fetchIssuesForProject
      ) {
        fetchIssuesForProject(supabaseClient, project.id, false);
      }
    }, [
      project?.id,
      currentProjectIssues,
      supabaseClient,
      fetchIssuesForProject,
    ]);

    const customProjectStatuses = useMemo(() => {
      if (!project?.statuses || !Array.isArray(project.statuses)) return [];
      return project.statuses;
    }, [project?.statuses]);

    const {
      projectStatusGroups,
      getStatusIcon,
      handleCreateStatus,
      handleUpdateStatus,
      handleDeleteStatus,
    } = useStatusManagement({
      projectData: project,
      currentProjectIssues,
      customProjectStatuses,
    });

    const columnConfig = useMemo(
      () => getColumnConfig(customProjectStatuses),
      [customProjectStatuses],
    );

    const renderTabContent = () => (
      <StatusesContent
        statusGroups={projectStatusGroups}
        getStatusIcon={getStatusIcon}
        onCreateStatus={handleCreateStatus}
        onUpdateStatus={handleUpdateStatus}
        onDeleteStatus={handleDeleteStatus}
        columnConfig={columnConfig}
      />
    );

    if (!project) {
      return (
        <div className="flex-1 flex items-center justify-center bg-background-primary">
          <p className="text-secondary-n-400">No project selected</p>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col h-full bg-background-primary">
        <div className="flex-1 min-h-0 overflow-y-auto">
          {renderTabContent()}
        </div>
      </div>
    );
  },
);

ProjectConfig.displayName = "ProjectConfig";

export default ProjectConfig;
